// Turns a tactical zone's buildings into scavengeable containers (V4 "search any
// building" model). A handful of buildings are "promising" — they
// hold the real haul — and the rest are ransacked (thin/empty). Promising
// buildings are DISTRIBUTED with min-spacing and biased away from the entry
// crossing, so a supply run can't be won by searching the two houses nearest the
// spawn and leaving: the food is spread across the map, which forces movement
// past the patrols (see the design thread / GAME_IDENTITY_V4.md).
//
// Legibility is the anti-tedium half: a building's promising/ransacked state
// is meant to be obvious from outside (rendered distinctly by tactical-view), so
// you route to the good ones instead of blind-scavenging every door. That state
// is no longer visible immediately: tactical-view gates the reveal on scout vision
// (a squad member's vision circle touching the footprint flips it from unknown
// to its real status, with a celebratory popup) — see SCOUT_VISION_RADIUS_METERS.
//
// Pure data + pure functions: geometry is passed in, contents are rolled from
// items.mjs, and all randomness flows through a seeded rng so the same zone +
// worldSeed always yields the same layout (the strategy-screen "~N promising
// buildings" preview must match the supply run you actually drop into). Food-aware
// content weighting is intentionally NOT here yet — that lands with the food/
// quota slice, which re-weights what promising buildings contain.

import { distanceInMeters } from "../road-graph.mjs";
import { rollItems } from "./items.mjs";

export const DEFAULT_MIN_PROMISING = 3;
export const DEFAULT_MAX_PROMISING = 6;
// Keep promising buildings from clustering on the same block...
export const DEFAULT_PROMISING_MIN_SPACING_METERS = 40;
// ...and off the doorstep of the entry crossing, so the map's interior always
// has a reason to exist.
export const DEFAULT_ENTRY_BIAS_METERS = 45;
// A promising building holds 1-3 items; a ransacked one is usually empty.
export const PROMISING_MIN_ITEMS = 1;
export const PROMISING_MAX_ITEMS = 3;
export const RANSACKED_ITEM_CHANCE = 0.25; // else nothing left worth grabbing

// --- Deterministic RNG (string seed -> mulberry32) ----------------------

function hashStringToSeed(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seedInt) {
  let state = seedInt >>> 0;
  return function next() {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- Building geometry --------------------------------------------------

function ringCentroid(ring) {
  let x = 0;
  let y = 0;
  for (const [lon, lat] of ring) { x += lon; y += lat; }
  return [x / ring.length, y / ring.length];
}

function featureCentroid(feature) {
  const geometry = feature?.geometry;
  if (!geometry) return null;
  if (geometry.type === "Polygon") return ringCentroid(geometry.coordinates[0]);
  if (geometry.type === "MultiPolygon") return ringCentroid(geometry.coordinates[0][0]);
  return null;
}

// Stable key for a building's scavenge pool. Exported so the supply-run view
// (tactical-view.mjs) and session builder look a building up the same way this
// module keyed it.
export function buildingKey(feature, index) {
  return feature.id ?? feature.properties?.osmId ?? `building:${index}`;
}

// --- Content rolls ------------------------------------------------------

function rollPromisingContents(rng) {
  const span = PROMISING_MAX_ITEMS - PROMISING_MIN_ITEMS + 1;
  const count = PROMISING_MIN_ITEMS + Math.floor(rng() * span);
  return rollItems({ count, rng });
}

function rollRansackedContents(rng) {
  return rng() < RANSACKED_ITEM_CHANCE ? rollItems({ count: 1, rng }) : [];
}

// --- Promising-building selection --------------------------------------

// Deterministic Fisher-Yates so the candidate order (and thus which buildings
// end up promising) is stable for a given seed but not tied to input order.
function shuffled(items, rng) {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// Greedily choose up to `targetCount` promising buildings that (a) sit at least
// `entryBiasMeters` from the entry crossing and (b) are at least
// `minSpacingMeters` apart from each other. If the spacing/entry filters are
// too strict to reach the target, relax them progressively rather than return
// fewer than we can — a supply run should still have its promised buildings.
function choosePromising(candidates, {
  targetCount, entryCoordinate, entryBiasMeters, minSpacingMeters, rng,
}) {
  const order = shuffled(candidates, rng);
  const chosen = [];

  const tryFill = ({ enforceEntryBias, spacing }) => {
    for (const candidate of order) {
      if (chosen.length >= targetCount) break;
      if (chosen.includes(candidate)) continue;
      if (enforceEntryBias && entryCoordinate
        && distanceInMeters(candidate.centroid, entryCoordinate) < entryBiasMeters) continue;
      if (chosen.some((existing) => distanceInMeters(existing.centroid, candidate.centroid) < spacing)) continue;
      chosen.push(candidate);
    }
  };

  tryFill({ enforceEntryBias: true, spacing: minSpacingMeters });
  if (chosen.length < targetCount) tryFill({ enforceEntryBias: false, spacing: minSpacingMeters });
  if (chosen.length < targetCount) tryFill({ enforceEntryBias: false, spacing: 0 });
  return chosen;
}

// --- Public API ---------------------------------------------------------

// Annotate every building in a zone with a scavenge descriptor keyed by a
// stable building id:
//   { buildingId, centroid, promising, ransacked, contents: [itemId, ...] }
// `contents` is what a unit can pull out (consumed as it scavenges). Returns a
// Map so callers can look a building's state up by id.
export function assignBuildingScavengeState({
  buildings = [],
  zoneId = "",
  worldSeed = "",
  entryCoordinate = null,
  minPromising = DEFAULT_MIN_PROMISING,
  maxPromising = DEFAULT_MAX_PROMISING,
  minSpacingMeters = DEFAULT_PROMISING_MIN_SPACING_METERS,
  entryBiasMeters = DEFAULT_ENTRY_BIAS_METERS,
  // Building ids that are always promising regardless of the random draw — the
  // "knows the area" perk (a survivor's revealedBuildingId, see poi-reveal.mjs)
  // guarantees its building holds a real haul. Doesn't perturb the rng stream:
  // contents are still rolled per-building in candidate order, just down the
  // promising branch for these.
  forcePromisingIds = [],
  rng,
} = {}) {
  const roll = rng ?? mulberry32(hashStringToSeed(`${worldSeed}:${zoneId}:scavenge`));
  const forced = forcePromisingIds instanceof Set ? forcePromisingIds : new Set(forcePromisingIds);

  const candidates = buildings
    .map((feature, index) => ({ id: buildingKey(feature, index), feature, centroid: featureCentroid(feature) }))
    .filter((entry) => Array.isArray(entry.centroid));

  const span = Math.max(0, maxPromising - minPromising);
  const targetCount = Math.min(candidates.length, minPromising + Math.floor(roll() * (span + 1)));

  const promising = new Set(choosePromising(candidates, {
    targetCount, entryCoordinate, entryBiasMeters, minSpacingMeters, rng: roll,
  }));

  const state = new Map();
  for (const candidate of candidates) {
    const isPromising = promising.has(candidate) || forced.has(candidate.id);
    // Roll contents for EVERY building (in candidate order) so the rng stream —
    // and therefore each building's contents — is stable for a given seed.
    const contents = isPromising ? rollPromisingContents(roll) : rollRansackedContents(roll);
    state.set(candidate.id, {
      buildingId: candidate.id,
      centroid: [...candidate.centroid],
      promising: isPromising,
      ransacked: !isPromising,
      contents,
    });
  }
  return state;
}

// Count of promising buildings — the strategy screen's "~N promising
// buildings here" hint, computed from the same deterministic assignment the
// supply run will use.
export function promisingCount(scavengeState) {
  let count = 0;
  for (const descriptor of scavengeState.values()) if (descriptor.promising) count += 1;
  return count;
}
